import "@css/temp_view2.css";
import type * as type from "@vTypes";
import type { ShiftManage } from "@vTypes/snapshot-type";
import { useCallback, useMemo } from "react";
import { Outlet } from "react-router-dom";
import useRosterConfig from "@/hooks/useRosterConfig";
import {
  buildRosterTableLayoutConfig,
  ROSTER_DAY_CELL_PX,
  ROSTER_GRID_GAP_PX,
} from "@/components/nurse/rosterColumnHoverLayout";
import RosterViewSkeleton from "./components/RosterViewSkeleton";
import useSnapShot from "@hooks/useSnapShot.ts";
import { useHolidayDatesQuery } from "@/hooks/useHolidayDatesQuery";
import {
  generatePreceptorGroupIDs,
  insertPreceptorData,
} from "@utils/preceptorUtils";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";
import { pre_monthlyNurseSchedule } from "@/utils/rosterCreate-util";
import type { RosterViewOutletContext } from "@/pages/roster-view/Roster_view_outlet_routes";
import type { IssuedAssignmentsViewBundle } from "@vTypes/snapshot-type";

import { useRosterViewNavigation } from "./hooks/useRosterViewNavigation";
import {
  RosterViewHeader,
  type RosterViewHeaderProps,
  RosterViewUnavailable,
} from "./components/RosterViewNavigation";

function Roster_view() {
  const navigation = useRosterViewNavigation();
  const { data: viewConfig, isPending: viewConfigPending } = useRosterConfig();
  const sheetLayout = buildRosterTableLayoutConfig({
    showPreceptorColumn: Boolean(viewConfig?.show_preceptor),
    showLevelColumn: Boolean(viewConfig?.show_level),
    showGradeColumn: false,
    showExperienceColumn: false,
  });
  // Date header includes a 1px leading border and a 40px toolbar with a 10px gap.
  const sheetWidth =
    sheetLayout.bodyFirstColPx +
    ROSTER_GRID_GAP_PX +
    new Date(navigation.date.year, navigation.date.month, 0).getDate() *
      ROSTER_DAY_CELL_PX +
    1 +
    40 +
    10;
  const {
    user: loginUser,
    date: currentScheduleDate,
    issuedList,
    groupId: issuedSnapshotGroupId,
  } = navigation;
  const selectedGroupId = issuedSnapshotGroupId;
  const { selectDate } = navigation;
  const setCurrentScheduleDateSynced = useCallback(
    (action: React.SetStateAction<typeof currentScheduleDate | undefined>) => {
      const next =
        typeof action === "function" ? action(currentScheduleDate) : action;
      if (next) selectDate(next);
    },
    [currentScheduleDate, selectDate]
  );
  const setIssuedSnapshotGroupId = navigation.selectWard;
  const { scope, myHref, allHref, wardOptions, selectWard } = navigation;
  const canSelectWard = Boolean(
    loginUser?.is_head_nurse ||
    loginUser?.is_master_admin ||
    loginUser?.EmpAuthGbn === "ADM" ||
    loginUser?.hn_auth === "HN"
  );
  const headerProps = useMemo(
    (): RosterViewHeaderProps => ({
      date: currentScheduleDate,
      onChange: selectDate,
      issuedList,
      scope,
      myHref,
      allHref,
      wardOptions,
      wardId: issuedSnapshotGroupId,
      onSelectWard: selectWard,
      canSelectWard,
    }),
    [
      currentScheduleDate,
      selectDate,
      issuedList,
      scope,
      myHref,
      allHref,
      wardOptions,
      issuedSnapshotGroupId,
      selectWard,
      canSelectWard,
    ]
  );

  const {
    data: rawSnapShotData,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useSnapShot({
    year: currentScheduleDate?.year,
    month: currentScheduleDate?.month,
    groupId: issuedSnapshotGroupId,
    enabled:
      navigation.scope === "all" &&
      navigation.wardAllowed &&
      !navigation.wardLoading,
  });

  const snapShotData = useMemo(() => {
    return rawSnapShotData;
  }, [rawSnapShotData]);

  const fullNurseSchema = snapShotData?.nurses;

  const snapshotAligned = useMemo(() => {
    if (
      snapShotData == null ||
      currentScheduleDate == null ||
      snapShotData.meta == null
    )
      return false;
    return (
      snapShotData.group_id === issuedSnapshotGroupId &&
      Number(snapShotData.meta.year) === Number(currentScheduleDate.year) &&
      Number(snapShotData.meta.month) === Number(currentScheduleDate.month)
    );
  }, [snapShotData, currentScheduleDate, issuedSnapshotGroupId]);

  const isRosterSnapshotLoading =
    currentScheduleDate != null &&
    loginUser != null &&
    navigation.scope === "all" &&
    navigation.wardAllowed &&
    !snapshotAligned &&
    (isPending || isFetching);

  const preceptorGroupID = useMemo(
    () => generatePreceptorGroupIDs(fullNurseSchema ?? []),
    [fullNurseSchema]
  );

  const { data: holidayDates = [] } = useHolidayDatesQuery(
    currentScheduleDate?.year,
    currentScheduleDate?.month
  );

  /** 발행 조회: 시프트 카탈로그는 스냅샷 `shifts`만 사용(발행 시점 정의). 로딩·미정렬 구간은 빈 배열 */
  const viewCatalogShifts = useMemo((): type.availableShifts[] => {
    if (!snapshotAligned || !snapShotData?.shifts?.length) return [];
    const byShiftId = new Map<string, (typeof snapShotData.shifts)[number]>();
    for (const s of snapShotData.shifts) {
      const prev = byShiftId.get(s.shift_id);
      if (!prev || s.id > prev.id) byShiftId.set(s.shift_id, s);
    }
    return [...byShiftId.values()] as unknown as type.availableShifts[];
  }, [snapshotAligned, snapShotData]);

  const { lookupMaps: shiftLookupMaps } = useShiftLookupMaps(
    viewCatalogShifts,
    null
  );

  const fullNurseSchemaAddPreceptor = useMemo(() => {
    if (!fullNurseSchema) return undefined;
    return insertPreceptorData(fullNurseSchema, preceptorGroupID);
  }, [fullNurseSchema, preceptorGroupID]);

  const outletContext = useMemo((): RosterViewOutletContext | null => {
    const rosterSheetResolved =
      snapshotAligned && snapShotData?.roster ? snapShotData.roster : null;

    const profileByNurseId = new Map(
      snapShotData?.nurses.map((nurse) => [nurse.nurse_id, nurse])
    );
    const monthlyScheduleResolved =
      snapshotAligned &&
      rosterSheetResolved &&
      fullNurseSchemaAddPreceptor != null
        ? pre_monthlyNurseSchedule(
            fullNurseSchemaAddPreceptor,
            rosterSheetResolved.nurses.map((row) => {
              const profile = profileByNurseId.get(row.nurse_id);
              return {
                ...row,
                team_id: profile?.team_id ?? null,
                team_name: profile?.team_name ?? null,
              };
            })
          )
        : null;

    const shiftManager: ShiftManage | ShiftManage[] = snapshotAligned
      ? snapShotData!.shift_manage
      : ([] as ShiftManage[]);

    const issuedAssignmentsView: IssuedAssignmentsViewBundle | null =
      snapshotAligned
        ? {
            assignments: null,
            groups: snapShotData!.groups ?? null,
            viewer:
              loginUser != null &&
              loginUser.nurse_id != null &&
              loginUser.nurse_id !== ""
                ? {
                    nurse_id: String(loginUser.nurse_id),
                    group_id: loginUser.group_id ?? "",
                    group_name:
                      (loginUser.mb_part_name ?? "").trim() ||
                      (loginUser.office_name ?? "").trim() ||
                      (loginUser.group_id ?? "").trim() ||
                      "",
                    office_id: loginUser.office_id ?? "",
                  }
                : null,
          }
        : null;

    return {
      headerProps,
      loginUser,
      issuedList,
      currentScheduleDate,
      setCurrentScheduleDate: setCurrentScheduleDateSynced,
      holidayDates,
      selectedGroupId: selectedGroupId ?? null,
      availableShifts: viewCatalogShifts,
      shiftLookupMaps,
      rosterSheet: rosterSheetResolved,
      monthlySchedule: monthlyScheduleResolved,
      shiftManager,
      issuedDate: snapshotAligned ? snapShotData!.created_at : undefined,
      issuedSnapshotId: snapshotAligned ? snapShotData?.snapshot_id : undefined,
      refreshIssuedRoster: refetch,
      scheduleId:
        navigation.scope === "my"
          ? navigation.personalScheduleId
          : snapshotAligned
            ? snapShotData!.schedule_id
            : undefined,
      issuedTeams:
        snapshotAligned && Array.isArray(snapShotData?.teams)
          ? snapShotData.teams
          : null,
      issuedMemo: snapshotAligned ? (snapShotData!.meta?.memo ?? "") : "",
      isRosterSnapshotLoading,
      issuedAssignmentsView,
      issuedSnapshotGroupId,
      setIssuedSnapshotGroupId,
    };
  }, [
    headerProps,
    snapshotAligned,
    snapShotData,
    currentScheduleDate,
    issuedList,
    fullNurseSchemaAddPreceptor,
    loginUser,
    holidayDates,
    selectedGroupId,
    viewCatalogShifts,
    shiftLookupMaps,
    isRosterSnapshotLoading,
    setCurrentScheduleDateSynced,
    issuedSnapshotGroupId,
    setIssuedSnapshotGroupId,
    navigation.scope,
    navigation.personalScheduleId,
    refetch,
  ]);

  const wholeError =
    navigation.scope === "all" &&
    (navigation.candidates.isError ||
      (navigation.wardAllowed && (navigation.wardMonths.isError || isError)));
  const forbidden =
    navigation.scope === "all" &&
    !navigation.wardLoading &&
    !navigation.wardAllowed;
  if (navigation.scope === "my" && viewConfigPending) {
    return (
      <div
        className="mx-auto w-full min-w-0"
        aria-label="근무표 화면 설정 불러오는 중"
        aria-busy
      >
        <RosterViewSkeleton />
      </div>
    );
  }
  return (
    <div
      className="font-pretendard mx-auto w-full min-w-0"
      style={{ maxWidth: sheetWidth + 40 }}
      data-testid="roster-view-page"
    >
      {navigation.scope === "all" &&
        (wholeError ||
          forbidden ||
          navigation.wardLoading ||
          navigation.wardMonths.isFetching ||
          isRosterSnapshotLoading ||
          !snapshotAligned ||
          !snapShotData?.roster?.nurses.length) && (
          <div className="px-5 pt-6 pb-4">
            <RosterViewHeader {...headerProps} />
          </div>
        )}
      {navigation.scope === "my" && navigation.personalMonths.isError && (
        <div
          role="alert"
          className="mx-5 mb-4 rounded-md border border-border p-3 text-sm"
        >
          내 근무표의 발행 월 목록을 불러오지 못했습니다.{" "}
          <button
            type="button"
            className="ml-2 underline focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => void navigation.personalMonths.refetch()}
          >
            다시 시도
          </button>
        </div>
      )}
      {wholeError ? (
        <RosterViewUnavailable
          message="근무표를 불러오지 못했습니다."
          onRetry={() => {
            if (navigation.candidates.isError)
              void navigation.candidates.refetch();
            if (navigation.wardAllowed && !navigation.wardLoading) {
              void navigation.wardMonths.refetch();
              void refetch();
            }
          }}
        />
      ) : forbidden ? (
        <RosterViewUnavailable message="이 병동의 전체 근무표를 조회할 수 없습니다." />
      ) : navigation.scope === "all" &&
        (navigation.wardLoading ||
          navigation.wardMonths.isFetching ||
          isRosterSnapshotLoading) ? (
        <RosterViewSkeleton />
      ) : navigation.scope === "all" &&
        (!snapshotAligned || !snapShotData?.roster?.nurses.length) ? (
        <RosterViewUnavailable
          showCalendarIcon
          message={`${navigation.wardOptions.find((ward) => ward.group_id === issuedSnapshotGroupId)?.group_name ?? "선택 병동"}의 ${currentScheduleDate.year}년 ${currentScheduleDate.month}월 근무표가 아직 발행되지 않았습니다.`}
        />
      ) : (
        <Outlet context={outletContext} />
      )}
    </div>
  );
}

export default Roster_view;
